import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Loader from "../components/Loader";
import Message from "../components/Message";
import {
  useGetUserWishlistQuery,
  useRemoveProductFromWishlistMutation,
} from "../slices/usersApiSlice";
import { toast } from "sonner";

const WishlistScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { data, isLoading, error, refetch } = useGetUserWishlistQuery(userInfo?._id, {
    skip: !userInfo?._id,
  });
  const [removeProductFromWishlist, { isLoading: removing }] =
    useRemoveProductFromWishlistMutation();
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [sortBy, setSortBy] = React.useState("recent");
  const [page, setPage] = React.useState(1);
  const pageSize = 8;

  React.useEffect(() => {
    setPage(1);
  }, [statusFilter, sortBy]);

  const handleRemove = async (productId) => {
    try {
      await removeProductFromWishlist(productId).unwrap();
      toast.success("Removed from wishlist");
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to remove wishlist item");
    }
  };

  const filtered = (data || []).filter((item) => {
    if (statusFilter === "available") return item.isAvailable;
    if (statusFilter === "unavailable") return !item.isAvailable;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "oldest") {
      return new Date(a.addedAt || 0) - new Date(b.addedAt || 0);
    }
    if (sortBy === "name-asc") {
      return String(a.product?.name || "").localeCompare(String(b.product?.name || ""));
    }
    if (sortBy === "name-desc") {
      return String(b.product?.name || "").localeCompare(String(a.product?.name || ""));
    }
    return new Date(b.addedAt || 0) - new Date(a.addedAt || 0);
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-2xl font-semibold text-slate-900">Wishlist</h2>

        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">{error?.data?.message || error.error}</Message>
        ) : (
          <>
            <div className="mb-2 mt-1 grid grid-cols-1 gap-3 sm:grid-cols-2 md:max-w-lg">
              <div>
                <label htmlFor="wishlistStatusFilter" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Status
                </label>
                <select
                  id="wishlistStatusFilter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
                >
                  <option value="all">All</option>
                  <option value="available">Available</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </div>

              <div>
                <label htmlFor="wishlistSort" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Sort
                </label>
                <select
                  id="wishlistSort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
                >
                  <option value="recent">Recently added</option>
                  <option value="oldest">Oldest added</option>
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                </select>
              </div>
            </div>

            <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600">
                      <th className="px-3 py-2 font-semibold">Product</th>
                      <th className="px-3 py-2 font-semibold">Price</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold">Added</th>
                      <th className="px-3 py-2 font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-3 py-6 text-center text-slate-500">
                          No wishlist items match this filter.
                        </td>
                      </tr>
                    ) : (
                      pageItems.map((item) => {
                        const product = item.product;
                        const priceValue = Number(product?.price || 0);
                        const isFree = Boolean(product?.isFree) || priceValue <= 0;
                        const productId = item.productId || product?._id;

                        return (
                          <tr key={item._id} className="border-b border-slate-100 text-slate-700">
                            <td className="px-3 py-2">{product?.name || "Unavailable product"}</td>
                            <td className="px-3 py-2">{isFree ? "Free" : `$${priceValue.toFixed(2)}`}</td>
                            <td className="px-3 py-2">{item.isAvailable ? "Available" : "Unavailable"}</td>
                            <td className="px-3 py-2">
                              {item.addedAt ? new Date(item.addedAt).toLocaleDateString("en-US") : "-"}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  disabled={!item.isAvailable || !productId}
                                  onClick={() => navigate(`/product/${productId}`)}
                                  className="rounded-lg border border-sky-300 bg-white px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Open
                                </button>
                                <button
                                  type="button"
                                  disabled={removing || !productId}
                                  onClick={() => handleRemove(productId)}
                                  className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="mt-3 flex items-center justify-between">
                <small className="text-xs text-slate-600">
                  Page {safePage} of {totalPages}
                </small>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default WishlistScreen;

